import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { GetCompanies, GetCompaniesVariables, SortDirectionEnum, useDebounce } from '@modmd/data'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE } from 'theme'
import { ROUTES } from 'internal-portal/constants/routes'
import { Box, Container } from 'components/Layout'
import Card from 'components/Card'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import Icon from 'components/Icon'
import Datalist from 'components/Datalist'
import Pagination from 'components/Pagination'
import TextInput from 'components/TextInput'
import { NewCompany } from './NewCompany'
import { GET_COMPANIES } from './operations'

type CompaniesProps = RouteComponentProps

const SKELETON_COLUMNS = 4
export const PAGE_LENGTH = 10

const Companies = ({ history }: CompaniesProps) => {
  const { DESC } = SortDirectionEnum
  const searchState = useSearchState<{
    page?: string
    address?: string
    nameOrGroupLike?: string
    direction?: SortDirectionEnum
  }>()
  const isTablet = useIsMinDevice(DEVICE.TABLET)
  const [getCompanies, { data: companiesData, loading: companiesLoading }] = useLazyQuery<
    GetCompanies,
    GetCompaniesVariables
  >(GET_COMPANIES)

  useDebounce(
    () => {
      getCompanies({
        variables: {
          pagination: {
            from: Number(searchState.data.page) * PAGE_LENGTH - PAGE_LENGTH || 0,
            length: PAGE_LENGTH,
            sortBy: 'id',
            direction: searchState.data.direction || DESC,
          },
          filterData: {
            nameOrGroupNameLike: searchState.data.nameOrGroupLike,
            address: searchState.data.address,
          },
        },
      })
    },
    500,
    [getCompanies, searchState.data]
  )

  const handleFilterInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      searchState.setSearchState({ [name]: value, page: 1 }, false, true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchState.setSearchState]
  )

  const companyCount = companiesData?.companies.pagination.total ?? 0
  const pageLength = companiesData?.companies.pagination.length ?? 0

  return (
    <DashboardPage
      Header={
        <Box display="flex" justifyContent="flex-end" p="1rem">
          <NewCompany />
        </Box>
      }
    >
      <Container>
        <Card mt="1rem">
          <Datalist
            isLoading={companiesLoading}
            skeletonProps={{
              columns: SKELETON_COLUMNS,
              rows: PAGE_LENGTH,
            }}
            data={companiesData?.companies?.companies || []}
            header={
              <Box
                display="grid"
                gridGap="1rem"
                gridTemplateColumns="repeat(auto-fit, minmax(10rem, max-content))"
              >
                <TextInput
                  name="nameOrGroupLike"
                  label="Company / Group"
                  value={searchState.data.nameOrGroupLike || ''}
                  onChange={handleFilterInputChange}
                  leftIcon={<Icon.Search />}
                  placeholder="Company name / group name"
                  hasBottomContent={false}
                />
                <TextInput
                  name="address"
                  label="Address"
                  value={searchState.data.address || ''}
                  onChange={handleFilterInputChange}
                  leftIcon={<Icon.Pin />}
                  placeholder="Search by Location"
                  hasBottomContent={false}
                />
              </Box>
            }
            shouldRenderTableHeader={isTablet}
            showActionIndicators
            renderTableHeader={() => (
              <Datalist.Row gridTemplateColumns="1fr 2fr 1fr 1fr">
                <Datalist.HeaderCell label="Company name" />
                <Datalist.HeaderCell label="Location" />
                <Datalist.HeaderCell label="Groups" />
                <Datalist.HeaderCell label="Members" />
              </Datalist.Row>
            )}
            renderTableRow={({ id, name, Address, Members, Groups }) => (
              <Datalist.Row
                display="grid"
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: '1fr 2fr 1fr 1fr',
                }}
                onClick={() => history.push(`/${ROUTES.COMPANIES}/${id}`)}
              >
                <Datalist.Cell label="Company name">{name}</Datalist.Cell>
                <Datalist.Cell label="Location">
                  {Address.street}, {Address.city}
                </Datalist.Cell>
                <Datalist.Cell label="Groups">{Groups.length}</Datalist.Cell>
                <Datalist.Cell label="Members">{Members.pagination.total}</Datalist.Cell>
              </Datalist.Row>
            )}
          />
        </Card>
        {companyCount > pageLength && (
          <Pagination.Wrapper>
            <Pagination
              firstPage={1}
              lastPage={Math.ceil(companyCount / pageLength)}
              currentPage={
                Math.ceil((companiesData?.companies.pagination.from || 0) / pageLength) + 1
              }
              onPageChange={(pageNumber) => {
                searchState.setSearchState({ page: pageNumber }, false)
              }}
            />
          </Pagination.Wrapper>
        )}
      </Container>
    </DashboardPage>
  )
}

export default Companies
